import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from 'auth/AuthProvider';
import { PERMISSIONS } from 'auth/permissions';
import { queryKeys } from 'core/api/queryKeys';
import { useI18n } from 'core/i18n/I18nProvider';
import { paths } from 'core/routing/paths';
import { assessmentApi } from 'features/assessment-dashboard/assessment.api';
import { Badge, EmptyState, ErrorState, PageHeader, TableSkeleton } from 'shared/components/Feedback';
import { FilterBar } from 'shared/components/FilterBar';
import { ListTable } from 'shared/components/ListTable';
import { RowActionsCell, RowActionsHeader } from 'shared/components/RowActionsMenu';
import { useConfirm } from 'shared/components/ConfirmDialog';
import { useToast } from 'shared/components/Toast';
import { humanizeApiError } from 'core/errors/mapFieldErrors';

export function PatternsPage() {
  const { t } = useI18n();
  const { hasPermission } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const canManage = hasPermission(PERMISSIONS.patternsManage);
  const query = useQuery({ queryKey: queryKeys.patterns, queryFn: ({ signal }) => assessmentApi.listPatterns({ signal }) });

  const activation = useMutation({
    mutationFn: ({ id, isActive }) => assessmentApi.setPatternActivation(id, isActive),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.patterns });
      toast.success(t('status.saved'));
    },
    onError: (error) => toast.error(humanizeApiError(error, t)),
  });

  const removal = useMutation({
    mutationFn: (id) => assessmentApi.deletePattern(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.patterns });
      toast.success(t('status.deleted'));
    },
    onError: (error) => toast.error(humanizeApiError(error, t)),
  });

  const items = useMemo(() => {
    const list = query.data?.items || [];
    return list
      .filter((item) => (status === 'all' ? true : status === 'active' ? item.is_active : !item.is_active))
      .filter((item) => {
        const haystack = `${item.slug} ${item.short_code || ''} ${item.name_ar} ${item.name_en}`.toLowerCase();
        return haystack.includes(search.trim().toLowerCase());
      })
      .sort((a, b) => a.sort_order - b.sort_order);
  }, [query.data, search, status]);

  if (query.isLoading) {
    return <TableSkeleton columns={7} rows={6} />;
  }
  if (query.isError) {
    return <ErrorState title={humanizeApiError(query.error, t)} onRetry={query.refetch} retryLabel={t('actions.retry')} />;
  }

  return (
    <div>
      <PageHeader
        title={t('patterns.title')}
        actions={canManage ? <Link className="btn btn-primary" to={paths.patternCreate}>{t('patterns.create')}</Link> : null}
      />
      <FilterBar
        onReset={() => { setSearch(''); setStatus('all'); }}
        resetLabel={t('actions.reset')}
        search={(
          <label className="field" htmlFor="pattern-search">
            <span className="field-label">{t('actions.search')}</span>
            <input id="pattern-search" className="input" value={search} onChange={(event) => setSearch(event.target.value)} placeholder={t('patterns.search')} />
          </label>
        )}
      >
        <label className="field" htmlFor="pattern-status">
          <span className="field-label">{t('actions.filter')}</span>
          <select id="pattern-status" className="input" value={status} onChange={(event) => setStatus(event.target.value)}>
            <option value="all">{t('filters.all')}</option>
            <option value="active">{t('filters.active')}</option>
            <option value="inactive">{t('filters.inactive')}</option>
          </select>
        </label>
      </FilterBar>
      {items.length === 0 ? (
        <EmptyState title={t('empty.patterns')} action={canManage ? <Link className="btn btn-primary" to={paths.patternCreate}>{t('patterns.create')}</Link> : null} />
      ) : (
        <ListTable caption={t('patterns.title')} stickyActions>
          <thead>
            <tr>
              <th>{t('patterns.color')}</th>
              <th>{t('patterns.nameAr')}</th>
              <th>{t('patterns.nameEn')}</th>
              <th>{t('patterns.slug')}</th>
              <th>{t('patterns.shortCode')}</th>
              <th>{t('patterns.active')}</th>
              <RowActionsHeader />
            </tr>
          </thead>
          <tbody>
            {items.map((pattern) => (
              <tr key={pattern.id}>
                <td><span className="color-dot" style={{ background: pattern.color }} /></td>
                <td>{pattern.name_ar}</td>
                <td>{pattern.name_en}</td>
                <td className="ltr-id">{pattern.slug}</td>
                <td><strong className="ltr-id">{pattern.short_code || '—'}</strong></td>
                <td>
                  {pattern.is_active ? t('patterns.active') : t('patterns.inactive')}
                  {pattern.has_pending_zero_warning ? <Badge tone="warning">{t('patterns.zeroWarning')}</Badge> : null}
                </td>
                <RowActionsCell
                  label={`${t('actions.rowActions')} #${pattern.id}`}
                  items={[
                    { key: 'weights', label: t('actions.configureWeights'), to: paths.weightMatrix(pattern.id) },
                    canManage ? { key: 'edit', label: t('actions.edit'), to: paths.patternEdit(pattern.id) } : null,
                    canManage ? {
                      key: 'activation',
                      label: pattern.is_active ? t('actions.deactivate') : t('actions.activate'),
                      disabled: activation.isPending,
                      onClick: () => activation.mutate({ id: pattern.id, isActive: !pattern.is_active }),
                    } : null,
                    canManage ? {
                      key: 'delete',
                      label: t('actions.delete'),
                      tone: 'danger',
                      disabled: removal.isPending,
                      onClick: async () => {
                        if (await confirm({ body: t('confirm.delete') })) {
                          removal.mutate(pattern.id);
                        }
                      },
                    } : null,
                  ]}
                />
              </tr>
            ))}
          </tbody>
        </ListTable>
      )}
    </div>
  );
}
